import { useEffect, useRef, useState } from "react";
import { Flame, ArrowRight } from "lucide-react";

/**
 * ReviveGrantedBanner — full-screen success overlay shown right after a
 * successful Monetag revive. Two purposes:
 *   1. Tell the player they've been revived (even if the ad tab is still up
 *      in front of them, the game tab is now showing this bright gold card).
 *   2. Flash the tab title ("🔥 REVIVED — Return to Dragon Tale ✧") so the
 *      game tab is trivially findable in a crowded browser tab bar.
 *
 * The player dismisses this by clicking "BACK TO DRAGON TALE" which resumes
 * their new active run.
 */
export default function ReviveGrantedBanner({ open, floor, health, maxHealth, onContinue }) {
  const [dismissing, setDismissing] = useState(false);
  const savedTitle = useRef(null);
  const flickerHandle = useRef(null);

  // Tab-title flicker: alternate between the alert and the original title so
  // the tab is highly visible in the browser's tab list.
  useEffect(() => {
    if (!open) return;
    if (typeof document === "undefined") return;
    savedTitle.current = document.title;
    let on = true;
    const alertTitle = "🔥 REVIVED — Return to the Vanguard ✧";
    document.title = alertTitle;
    flickerHandle.current = setInterval(() => {
      on = !on;
      document.title = on ? alertTitle : (savedTitle.current || "Champions of the Dawn Vanguard");
    }, 1200);
    return () => {
      if (flickerHandle.current) clearInterval(flickerHandle.current);
      if (savedTitle.current) document.title = savedTitle.current;
    };
  }, [open]);

  // If the tab regains focus (user Cmd/Alt-Tabs back OR closes the ad tab),
  // stop flickering the title — they're back.
  useEffect(() => {
    if (!open) return;
    const onFocus = () => {
      if (flickerHandle.current) { clearInterval(flickerHandle.current); flickerHandle.current = null; }
      if (savedTitle.current) document.title = savedTitle.current;
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "visible") onFocus();
    });
    return () => {
      window.removeEventListener("focus", onFocus);
    };
  }, [open]);

  if (!open) return null;

  const handleContinue = () => {
    setDismissing(true);
    setTimeout(() => { onContinue?.(); setDismissing(false); }, 220);
  };

  return (
    <div
      className="fixed inset-0 z-[75] flex items-center justify-center px-4"
      data-testid="revive-granted-banner"
      style={{
        background: "radial-gradient(circle at 50% 50%, rgba(212,175,55,0.28) 0%, rgba(20,10,0,0.94) 60%, rgba(5,3,1,0.98) 100%)",
        opacity: dismissing ? 0 : 1,
        transition: "opacity 200ms ease-out",
      }}
    >
      <div
        className="relative max-w-md w-full panel p-8 text-center border-2"
        style={{
          borderColor: "#d4af37",
          boxShadow: "0 0 80px rgba(212,175,55,0.5), inset 0 0 40px rgba(212,175,55,0.10)",
          animation: "revive-pop 0.55s cubic-bezier(0.20,0.90,0.30,1.15) both",
        }}
      >
        {/* Ember halo behind the card */}
        <div
          className="absolute inset-0 pointer-events-none opacity-70"
          style={{
            background: "radial-gradient(120px 60px at 50% 30%, rgba(226,88,34,0.45), transparent 70%)",
            animation: "revive-halo 2.4s ease-in-out infinite",
          }}
        />

        <div className="relative z-10">
          <div className="flex items-center justify-center gap-2 mb-2">
            <Flame size={16} className="ember" />
            <span className="text-[10px] tracking-[0.5em] gold-text">— REVIVE GRANTED —</span>
            <Flame size={16} className="ember" />
          </div>

          <h2
            className="font-heading italic gold-text"
            style={{
              fontSize: "clamp(2.5rem, 6vw, 4rem)",
              lineHeight: 1,
              textShadow: "0 0 30px rgba(212,175,55,0.7)",
            }}
          >
            You Return.
          </h2>

          <p className="font-narrative text-base ash-text mt-4 italic">
            The sponsor's bargain is struck. You rise on floor <span className="gold-text">{floor}</span> with
            {" "}<span className="ember">{health}/{maxHealth} HP</span>.
          </p>

          <div className="mt-6 text-[10px] tracking-[0.35em] ash-text">
            YOUR REWARD IS SECURED · PRESS ON
          </div>

          <button
            onClick={handleContinue}
            data-testid="revive-back-to-game-btn"
            className="btn-primary mt-6 w-full text-base flex items-center justify-center gap-3"
            style={{
              borderColor: "#d4af37",
              background: "linear-gradient(180deg, rgba(212,175,55,0.20), rgba(30,20,5,0.9))",
              animation: "revive-btn-pulse 2.0s ease-in-out infinite",
            }}
          >
            <span>BACK TO THE DUNGEON</span>
            <ArrowRight size={16} />
          </button>

          <p className="text-[10px] tracking-widest ash-text mt-3 opacity-70">
            The dungeon waits.
          </p>
        </div>

        <style>{`
          @keyframes revive-pop {
            0%   { opacity: 0; transform: translateY(30px) scale(0.92); }
            60%  { opacity: 1; }
            100% { opacity: 1; transform: translateY(0)    scale(1); }
          }
          @keyframes revive-halo {
            0%,100% { opacity: 0.4; transform: scale(1); }
            50%     { opacity: 0.85; transform: scale(1.15); }
          }
          @keyframes revive-btn-pulse {
            0%,100% { box-shadow: 0 0 0 rgba(212,175,55,0.0); }
            50%     { box-shadow: 0 0 30px rgba(212,175,55,0.6); }
          }
        `}</style>
      </div>
    </div>
  );
}
